/**
 * HDR fusion backend selector.
 *
 * Three rungs, picked at first call and cached for the lifetime of the page:
 *
 *   webgl2-32f   — WebGL2 + EXT_color_buffer_float, RGBA32F render targets
 *   webgl2-16f   — WebGL2 + EXT_color_buffer_half_float, RGBA16F render targets
 *                  (≥1024 ULPs of precision loss vs 32f; flagged so the UI can
 *                  show a "reduced precision" badge but functionally identical)
 *   cpu          — Lane A's `mertens.js` running in JS on the main / worker thread
 *
 * The selector NEVER throws. A WebGL2 init failure (no GL2, no float
 * extension, or any compile/link error) silently falls through to CPU. UI
 * code reads `gpuAvailable` to decide whether to expose the backend toggle.
 *
 * Memoisation: the WebGL2 instance is reused across `fuseBracketsAuto` calls.
 * A future SPA navigation that re-imports this module would re-initialise.
 * `disposeBackend()` clears the cache (used by tests + Lane C teardown).
 */

import { fuseBrackets as fuseBracketsCPUInner } from './mertens.js';
import { ingestSRGB } from '../ingest.js';
import { WebGL2Backend } from './gpu/webgl2.js';

/**
 * @typedef {('webgl2-32f' | 'webgl2-16f' | 'cpu')} BackendId
 */

let cachedInstance = null;
let cachedId = null;

/**
 * Resolve the best available backend. Lazy — first call probes WebGL2; later
 * calls return the memoised result. Pass `{ force: true }` to re-probe.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.force]   re-run probing even if a cached instance exists
 * @param {boolean} [opts.preferCPU]  skip GPU probing entirely (testing aid)
 * @returns {{ backend: BackendId, instance: WebGL2Backend | null }}
 */
export function selectBackend(opts = {}) {
    if (!opts.force && cachedId !== null) {
        return { backend: cachedId, instance: cachedInstance };
    }
    if (opts.preferCPU) {
        cachedInstance = null;
        cachedId = 'cpu';
        return { backend: cachedId, instance: null };
    }

    /* Probe environment: do we have a GL2 context at all? Some headless
     * runtimes (Node without a canvas implementation) expose `WebGL2RenderingContext`
     * on globalThis but can't actually create a context. We try, and on
     * failure fall through to CPU. */
    let instance = null;
    try {
        instance = new WebGL2Backend();
        const ok = instance.initialize();
        if (!ok) {
            instance.dispose();
            instance = null;
        }
    } catch (_e) {
        if (instance) try { instance.dispose(); } catch (__) { /* ignore */ }
        instance = null;
    }

    if (instance && instance.precision === '32f') {
        cachedInstance = instance;
        cachedId = 'webgl2-32f';
    } else if (instance && instance.precision === '16f') {
        cachedInstance = instance;
        cachedId = 'webgl2-16f';
    } else {
        cachedInstance = null;
        cachedId = 'cpu';
    }
    return { backend: cachedId, instance: cachedInstance };
}

/**
 * Boolean snapshot of GPU availability. Reads the cache; `selectBackend()`
 * runs implicitly on first call. Useful for badge UI ("Backend: WebGL2 RGBA32F").
 *
 * @returns {boolean}
 */
export function gpuAvailable() {
    const { backend } = selectBackend();
    return backend === 'webgl2-32f' || backend === 'webgl2-16f';
}

/**
 * Drop the cached backend instance and re-probe on next call. Used by tests
 * that need to swap between CPU and GPU paths in the same page.
 */
export function disposeBackend() {
    if (cachedInstance) {
        try { cachedInstance.dispose(); } catch (_) { /* ignore */ }
        cachedInstance = null;
    }
    cachedId = null;
}

/**
 * Always-GPU entry — throws if no GPU backend is available. Exposed so test
 * harnesses (and later, UI) can force the GPU path. Returns a Promise so
 * the signature mirrors `mergeBrackets` / future async operators.
 *
 * Codex 2.A.2.design HIGH-2: the "Auto" in `fuseBracketsAuto` is the
 * GPU/CPU axis. `opts.fusionMode='auto'` is the camera/robust axis and
 * MUST be resolved upstream by `auto-detect.js` before reaching either
 * backend. Both paths forward `opts` verbatim — the actual
 * `fusionMode: 'camera' | 'robust'` switch is handled by the kernel.
 *
 * @param {Array<{rgba?:Float32Array,data?:Uint8ClampedArray|Uint8Array,width:number,height:number}>} stack
 * @param {object} [opts]   Forwarded verbatim — `fusionMode: 'camera' | 'robust'`
 *                          (default 'camera'), plus all existing HDR opts.
 * @returns {Promise<{ rgba: Float32Array, width: number, height: number, backend: BackendId }>}
 */
export async function fuseBracketsGPU(stack, opts = {}) {
    const { backend, instance } = selectBackend();
    if (!instance) {
        throw new Error('fuseBracketsGPU: no GPU backend available (' + backend + ')');
    }
    const result = instance.fuseBrackets(stack, opts);
    return { ...result, backend };
}

/**
 * Auto-routing entry — picks the best available backend, dispatches, and
 * returns a result tagged with which backend ran. Falls through to CPU
 * silently on GPU failure (incl. AbortError? — no, AbortError propagates;
 * only init / compile / runtime GL exceptions trigger fallback).
 *
 * Sync inputs (Float32Array or Uint8 ImageData-like) handled identically;
 * the GPU backend ingests Uint8 internally, the CPU adapter does the same.
 *
 * @param {Array<{rgba?:Float32Array,data?:Uint8ClampedArray|Uint8Array,width:number,height:number}>} stack
 * @param {object} [opts]
 * @returns {Promise<{ rgba: Float32Array, width: number, height: number, backend: BackendId }>}
 */
export async function fuseBracketsAuto(stack, opts = {}) {
    const { backend, instance } = selectBackend();
    if (instance) {
        try {
            const result = instance.fuseBrackets(stack, opts);
            return { ...result, backend };
        } catch (err) {
            /* AbortError is intentional — never silence. */
            if (err && err.name === 'AbortError') throw err;
            /* Phase 2.A.2: GpuFeatureUnsupported is a one-shot fallback —
             * the GPU backend is fine, this specific call just hit a
             * feature gap (e.g. fusionMode='robust' on GPU, deferred to
             * 2.A.3). Keep cached instance alive and just fall through. */
            if (err && err.name === 'GpuFeatureUnsupported') {
                /* Fall through to CPU for THIS call only. */
            } else {
                /* Hard failure — mark the GPU backend bad; subsequent
                 * calls go straight to CPU. */
                try { instance.dispose(); } catch (_) { /* ignore */ }
                cachedInstance = null;
                cachedId = 'cpu';
            }
            /* Fall through to CPU. */
        }
    }
    /* CPU path — normalise input shapes the same way `fuseBracketsCPU` does
     * in the public adapter: Uint8 → ingestSRGB → Float32 ACEScg. */
    const f32Stack = stack.map((b, i) => {
        if (!b || !b.width || !b.height) {
            throw new Error(`fuseBracketsAuto: bracket[${i}] missing width/height`);
        }
        if (b.rgba instanceof Float32Array) {
            return { rgba: b.rgba, width: b.width, height: b.height };
        }
        if (b.data instanceof Uint8ClampedArray || b.data instanceof Uint8Array) {
            return { rgba: ingestSRGB(b.data, b.width, b.height), width: b.width, height: b.height };
        }
        throw new Error(`fuseBracketsAuto: bracket[${i}] needs .rgba or .data`);
    });
    const result = fuseBracketsCPUInner(f32Stack, opts);
    return { ...result, backend: 'cpu' };
}
